import React, { useState } from "react";
import { CepInput } from "../components/CepInput";
import { observer } from "mobx-react-lite";
import { store } from "../stores/Store";
import { formatBRL } from "../utils";

export const CartPage: React.FC = observer(() => {
  const {
    cartItems,
    cartTotal,
    cartItemsCount,
    updateQuantity,
    removeFromCart,
    calculaFrete,
  } = store;

  const [frete, setFrete] = useState<number | null>(null);
  const [prazoFrete, setPrazoFrete] = useState<number | null>(null);
  const [cep, setCep] = useState("");
  const [calculando, setCalculando] = useState(false);
  const [envio, setEnvio] = useState<"SEDEX" | "PAC">("SEDEX");
  const [cepError, setCepError] = useState<string | null>(null);

  const handleQuantityChange = (productId: number, newQuantity: number) => {
    updateQuantity(productId, newQuantity);
  };

  const handleCalcularFrete = async (e: React.FormEvent) => {
    e.preventDefault();
    setCepError(null);

    // Remove tudo que não for número
    const cepNumeros = cep.replace(/\D/g, "");

    // Validação de CEP brasileiro (8 dígitos)
    if (!/^\d{8}$/.test(cepNumeros)) {
      setCepError("Digite um CEP válido (8 dígitos, apenas números).");
      setFrete(null);
      setPrazoFrete(null);
      return;
    }

    setCalculando(true);
    try {
      const codProdutos = cartItems.map((item) => item.id);
      const result = await calculaFrete(codProdutos, cepNumeros, envio); // Use o CEP só com números
      setFrete(result.custoFrete);
      setPrazoFrete(result.prazoFrete);
    } catch {
      setFrete(null);
      setPrazoFrete(null);
      alert("Erro ao calcular frete. Verifique o CEP.");
    }
    setCalculando(false);
  };

  if (cartItems.length === 0) {
    return (
      <div className="container-fluid bg_branco">
        <div className="container ctn_default">
          <div className="row">
            <div
              className="col-md-8 col-md-push-2 text-center"
              style={{ padding: "100px 0" }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "64px", color: "#666" }}
              >
                shopping_cart
              </span>
              <h2 className="titulo titulo_25 titulo_400 txt_preto mg-bt_20">
                Sem itens no carrinho
              </h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid bg_branco">
      <div className="container ctn_default">
        <div className="row">
          <div className="col-md-8 col-md-push-2">
            <h1 className="titulo titulo_25 titulo_400 txt_preto mg-bt_20">
              <span className="material-symbols-outlined">shopping_cart</span>
              Meu Carrinho
            </h1>

            <div className="ctn_value mg-bt_20">
              <div>
                <p>
                  <b>Quantidade</b>
                </p>
              </div>
              <div>
                <p>{cartItemsCount} itens</p>
              </div>
            </div>

            <div className="mg-bt_40">
              {cartItems.map((item) => (
                <div key={item.id} className="ctn_item">
                  <div className="ctn_img">
                    <img src={item.fotoCapaUrl} alt={item.nome} />
                  </div>
                  <div className="ctn_infos">
                    <h3 className="titulo_15 titulo_700 txt_preto">
                      {item.nome}
                    </h3>
                    <p>Por {item.autores}</p>
                  </div>
                  <div className="ctn_counter">
                    <h4 className="preco titulo_25 titulo_700 txt_cor-id-1 mg-bt_10">
                      {formatBRL(item.preco * item.quantidade)}
                    </h4>
                    <div className="counter">
                      <div
                        className="counter-menos"
                        onClick={() =>
                          handleQuantityChange(item.id, item.quantidade - 1)
                        }
                      >
                        <div></div>
                      </div>
                      <div>
                        <input
                          type="number"
                          className="counter-input"
                          value={item.quantidade}
                          min="1"
                          onChange={(e) =>
                            handleQuantityChange(
                              item.id,
                              parseInt(e.target.value) || 1
                            )
                          }
                        />
                      </div>
                      <div
                        className="counter-mais"
                        onClick={() =>
                          handleQuantityChange(item.id, item.quantidade + 1)
                        }
                      >
                        <div></div>
                      </div>
                    </div>
                    <div style={{ textAlign: "center", marginTop: "20px" }}>
                      <button
                        className="bt bt_cor-id-2"
                        onClick={() => removeFromCart(item.id)}
                        style={{ minWidth: "120px", background: "#FF17BA" }}
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="titulo titulo_25 titulo_400 txt_preto mg-bt_20">
              <span className="material-symbols-outlined">local_shipping</span>
              Forma de Envio
            </h2>

            <div className="ctn_radios mg-bt_20">
              <div>
                <input
                  type="radio"
                  id="correios-sedex"
                  name="envio"
                  value="SEDEX"
                  checked={envio === "SEDEX"}
                  onChange={() => setEnvio("SEDEX")}
                />
                <label
                  htmlFor="correios-sedex"
                  className="titulo_15 titulo_700"
                >
                  Correios SEDEX
                </label>
              </div>
              <div>
                <input
                  type="radio"
                  id="correios-pac"
                  name="envio"
                  value="PAC"
                  checked={envio === "PAC"}
                  onChange={() => setEnvio("PAC")}
                />
                <label htmlFor="correios-pac" className="titulo_15 titulo_700">
                  Correios PAC
                </label>
              </div>
            </div>

            <div className="form_default ctn_cep mg-bt_40">
              <form onSubmit={handleCalcularFrete}>
                <div
                  className="form_row row_3-1"
                  style={{ alignItems: "center" }}
                >
                  <div>
                    <label className="titulo_15 titulo_700">CEP</label>
                    <CepInput
                      name="cep"
                      required
                      value={cep}
                      onChange={setCep}
                      disabled={calculando}
                    />
                    <div
                      style={{ minHeight: 24, marginTop: 8, color: "#198754" }}
                    >
                      {prazoFrete !== null
                        ? `Prazo estimado ${prazoFrete} dia(s)`
                        : ""}
                    </div>
                    {cepError && (
                      <div style={{ color: "#dc3545", marginBottom: 8 }}>
                        {cepError}
                      </div>
                    )}
                  </div>
                  <div
                    className="ctn_bt"
                    style={{ padding: "0 10px", marginBottom: "9px" }}
                  >
                    <input
                      className="bt bt_cor-id-3 bt_full"
                      style={{ padding: "8px 0px" }}
                      type="submit"
                      value={calculando ? "Calculando..." : "Calcular frete"}
                      disabled={calculando}
                    />
                  </div>
                </div>
              </form>
            </div>

            <h2 className="titulo titulo_25 titulo_400 txt_preto mg-bt_20">
              <span className="material-symbols-outlined">shopping_bag</span>
              Total Pedido
            </h2>

            <div className="ctn_value mg-bt_10">
              <div>
                <p className="titulo_25">
                  <b>Subtotal:</b>
                </p>
              </div>
              <div>
                <p className="titulo_25">
                  {formatBRL(
                    cartItems.reduce(
                      (total, item) => total + item.quantidade * item.preco,
                      0
                    )
                  )}
                </p>
              </div>
            </div>

            {frete !== null && (
              <div className="ctn_value mg-bt_10">
                <div>
                  <p className="titulo_25">
                    <b>Frete:</b>
                  </p>
                </div>
                <div>
                  <p className="titulo_25">{formatBRL(frete)}</p>
                </div>
              </div>
            )}

            <div className="ctn_value mg-bt_10">
              <div>
                <p className="titulo_25">
                  <b>Descontos:</b>
                </p>
              </div>
              <div>
                <p className="titulo_25">
                  {formatBRL(
                    cartItems.reduce(
                      (total, item) => total + item.desconto * item.quantidade,
                      0
                    )
                  )}
                </p>
              </div>
            </div>

            <div className="ctn_value mg-bt_40">
              <div>
                <p className="titulo_25">
                  <b>Total:</b>
                </p>
              </div>
              <div>
                <p className="titulo_25 txt_cor-id-1">
                  <b>{formatBRL(cartTotal + (frete ?? 0))}</b>
                </p>
              </div>
            </div>

            <div className="ctn_bts">
              <div>
                <a href="/" className="bt bt_big">
                  Continuar comprando
                </a>
              </div>
              <div>
                <button
                  type="button"
                  className="bt bt_big"
                  style={{
                    pointerEvents: frete === null ? "none" : undefined,
                    opacity: frete === null ? 0.5 : 1,
                  }}
                  tabIndex={frete === null ? -1 : 0}
                  onClick={() => {
                    if (frete !== null) {
                      store.startCheckout(cep, frete, envio);
                    }
                  }}
                  disabled={frete === null}
                >
                  Finalizar Pedido
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
